import React from "react";
import { useWatch } from "react-hook-form";

import FormTitle from "components/Form/Title";
import FormErrors from "pages/ApplicationForm/components/FormErrors";
import { getApplicantName } from "pages/ApplicationForm/utils";
import { StepProps } from "../../types";
import FormInputCurrency from "components/Form/Input/Currency";
import FormInputRadio from "components/Form/Input/Radio";
import FormInputTextArea from "components/Form/Input/TextArea";
import { checkTruthyValue } from "utils/validation";
import { LABELS } from "./labels";
import ApplicantCredit from "./ApplicantCredit";

const STEP_KEY = "expenditure";

const ExpenditureForm: React.FC<StepProps> = ({
  control,
  register,
  errors,
  setValue,
  applicantsFieldArray,
}) => {
  const householdExpenditureKey = `${STEP_KEY}.householdExpenditure`;
  const applicationExpenditureErrorKey = errors?.[STEP_KEY] as any;
  const householdExpenditureErrorKey =
    applicationExpenditureErrorKey?.householdExpenditure;

  const watchExpenditure = useWatch({ name: STEP_KEY, control: control });

  const stepErors = [
    errors?.[STEP_KEY]?.householdExpenditure,
    errors?.[STEP_KEY]?.applicantCredit,
  ].filter(Boolean);

  return (
    <>
      <div className="mb-4 flex flex-col space-y-4">
        <FormTitle left border title="Expenditure" mb={false} />

        <FormErrors formErrors={stepErors} />

        {!applicantsFieldArray ||
        (applicantsFieldArray && applicantsFieldArray?.length === 0) ? (
          <p className="pt-4 text-center">
            Please first submit Applicant Details to complete this step.
          </p>
        ) : (
          applicantsFieldArray.map((applicant, k) => {
            const applicantNumber = k + 1;
            const idKey = `${STEP_KEY}.applicantCredit.${k}`;
            const errorKey = errors?.[STEP_KEY]?.applicantCredit?.[k];

            return (
              <div key={applicant.id} className="pb-4 last:pb-0">
                <FormTitle
                  left
                  title={`Applicant: ${
                    getApplicantName(applicant) || applicantNumber
                  }`}
                />

                <input
                  {...register(`${STEP_KEY}.applicantCredit.${k}.applicantId`, {
                    value: applicant.uuid,
                  })}
                  type="hidden"
                />

                <ApplicantCredit
                  register={register}
                  control={control}
                  idKey={idKey}
                  errorKey={errorKey}
                  setValue={setValue}
                />
              </div>
            );
          })
        )}
      </div>
      <div className="flex flex-col space-y-4">
        <FormTitle left title="Household Expenditure" mb={false} />

        <div className="grid grid-cols-2 gap-4 pb-2">
          <FormInputCurrency
            id={`${householdExpenditureKey}.foodDrinkGroceries`}
            label={LABELS.foodDrinkGroceries}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.foodDrinkGroceries?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.clothingFootwearPersonal`}
            label={LABELS.clothingFootwearPersonal}
            register={register}
            requiredField={false}
            error={
              householdExpenditureErrorKey?.clothingFootwearPersonal?.message
            }
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.utilitiesOtherHousing`}
            label={LABELS.utilitiesOtherHousing}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.utilitiesOtherHousing?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.furnishingsHouseholdMaintenance`}
            label={LABELS.furnishingsHouseholdMaintenance}
            register={register}
            requiredField={false}
            error={
              householdExpenditureErrorKey?.furnishingsHouseholdMaintenance
                ?.message
            }
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.transportTravel`}
            label={LABELS.transportTravel}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.transportTravel?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.communication`}
            label={LABELS.communication}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.communication?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.recreationRestaurantsHotels`}
            label={LABELS.recreationRestaurantsHotels}
            register={register}
            requiredField={false}
            error={
              householdExpenditureErrorKey?.recreationRestaurantsHotels?.message
            }
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.insurance`}
            label={LABELS.insurance}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.insurance?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.councilTax`}
            label={LABELS.councilTax}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.councilTax?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.cigarettesTobacco`}
            label={LABELS.cigarettesTobacco}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.cigarettesTobacco?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.childcareSchoolFees`}
            label={LABELS.childcareSchoolFees}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.childcareSchoolFees?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.adultCare`}
            label={LABELS.adultCare}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.adultCare?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.maintenanceCsa`}
            label={LABELS.maintenanceCsa}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.maintenanceCsa?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.mortgageEndowment`}
            label={LABELS.mortgageEndowment}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.mortgageEndowment?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.health`}
            label={LABELS.health}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.health?.message}
            setValue={setValue}
          />

          <FormInputCurrency
            id={`${householdExpenditureKey}.other`}
            label={LABELS.other}
            register={register}
            requiredField={false}
            error={householdExpenditureErrorKey?.other?.message}
            setValue={setValue}
          />
        </div>

        <FormInputRadio
          id={`${householdExpenditureKey}.futureChangeInExpenditure`}
          label={LABELS.futureChangeInExpenditure}
          register={register}
          requiredField={false}
          initValue={
            watchExpenditure?.householdExpenditure?.futureChangeInExpenditure
          }
          error={
            applicationExpenditureErrorKey?.futureChangeInExpenditure?.message
          }
        />

        {checkTruthyValue(
          watchExpenditure?.householdExpenditure?.futureChangeInExpenditure,
        ) && (
          <FormInputTextArea
            id={`${householdExpenditureKey}.changeInExpenditureDetail`}
            label={LABELS.changeInExpenditureDetail}
            register={register}
            requiredField={false}
            error={
              applicationExpenditureErrorKey?.changeInExpenditureDetail?.message
            }
          />
        )}
      </div>
    </>
  );
};

export default ExpenditureForm;
